/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useContext} from 'react'
import {IntlContext} from 'react-intl'
import PropTypes from 'prop-types'
import {Icon, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.

import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-down.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-right.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-left.svg'
import '@salesforce/retail-react-app/app/assets/svg/file.svg'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.

import {vbrandViewBoxes} from './shared/logo'
import {BRAND_KEY} from '../../brand'

import '../../assets/svg/brand-logo-default.svg'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 * @param {Object} passProps - props that will be passed onto the underlying Icon component
 * @param {Object} localizationAttributes - attributes with localized values that will be passed
 *      onto the underlying Icon component, use `defineMessage` to create localized string.
 *      Additionally, if the icon is rendered outside the provider tree, you'll also need to
 *      pass an intl object from react-intl as a prop to translate the messages.
 */
/* istanbul ignore next */
export const icon = (name, passProps, localizationAttributes) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const theme = useTheme()
        // NOTE: We want to avoid `useIntl` here because that throws when <IntlProvider> is not in
        // the component ancestry, but we only enforce `intl` if we have `localizationAttributes`.
        let intl = useContext(IntlContext)
        if (localizationAttributes) {
            if (props?.intl) {
                const {intl: intlProp, ...otherProps} = props
                // Allow `props.intl` to take precedence over the intl we found
                intl = intlProp
                props = otherProps
            }
            if (!intl) {
                throw new Error(
                    'To localize messages, you must either have <IntlProvider> in the component ancestry or provide `intl` as a prop',
                )
            }
            Object.keys(localizationAttributes).forEach((key) => {
                passProps[key] = intl.formatMessage(localizationAttributes[key])
            })
        }
        const baseStyle = theme?.components?.Icon?.baseStyle

        //If Icon is a logo, we don't set aria-label and mark as aria-hidden
        const isLogo = name === 'brand-logo-default' || name === 'brand-logo-inverted'
        const ariaLabelNameIcon = isLogo ? undefined : name

        return (
            <Icon
                ref={ref}
                role="img"
                aria-label={ariaLabelNameIcon}
                aria-hidden={isLogo ? true : undefined}
                {...baseStyle}
                {...props}
                {...passProps}
            >
                <use role="presentation" xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        intl: PropTypes.object,
    }

    component.displayName = `${displayName}Icon`
    return component
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const BrandLogo = icon('brand-logo-default', {viewBox: vbrandViewBoxes[BRAND_KEY]})
export const BrandLogoInverted = icon('brand-logo-inverted', {viewBox: vbrandViewBoxes[BRAND_KEY]})
export const ChevronDownIcon = icon('chevron-down')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
/* export const ChevronLeftHeaderIcon = icon('chevronLeft')
export const ChevronRightHeaderIcon = icon('chevronRight') */
export const ChevronUpIcon = icon('chevron-up')
export const FileIcon = icon('file')

import * as sharedIcons from './shared'
import * as overridesIcons from './extra'

export const {
    A1EnergyIcon,
    A2EnergyIcon,
    A3EnergyIcon,
    AEnergyIcon,
    AccountIcon,
    AccessoriesCountIcon,
    AddIcon,
    AdviceIcon,
    AlertIcon,
    AlertIconAlternative, // only in kenwood
    AmericanexpressIcon,
    ApplepayIcon,
    AppstoreIcon,
    AssistanceIcon,
    BEnergyIcon,
    BenefitDeliveryIcon,
    BestSellerIcon,
    BeveragesIcon,
    BurgerIcon,
    BreadcrumbSeperatorIcon,
    CEnergyIcon,
    CalendarIcon,
    CareShieldIcon,
    CartIcon,
    CheckIcon,
    CheckIconToggleCard,
    EcoPartIcon,
    InputCheckIcon,
    SwatchCheckIcon,
    CheckCircleIcon,
    ChefIcon,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronLeftHeaderIcon, //import changed, old one is commented above
    ChevronRightHeaderIcon, //import changed, old one is commented above
    ChevronUp,
    DoubleChevronRightIcon,
    UpgradeCartIcon,
    Close16Icon,
    Close16FilledIcon,
    CloseIcon,
    ColdIcon,
    CompareIcon,
    CopyIcon,
    CrossIcon,
    DEnergyIcon,
    DeleteIcon,
    DiscountIcon,
    DownIcon,
    DownloadDocumentIcon, //only in kenwood
    DownloadIcon,
    EditIcon,
    EmailIcon,
    ExclamationIcon,
    FacebookIcon,
    FavouriteFilledIcon,
    FavouriteIcon,
    FaqIconAlternative, // only in kenwood
    FaqIcon,
    FilterIcon,
    FiltersIcon,
    FoodIcon,
    GoBackIcon,
    GooglePlayIcon,
    GridIconA,
    GridIconB,
    GuaranteedIcon,
    HeaderDown,
    HeaderRight,
    HeaderUp,
    GalleryChevronLeftIcon,
    GalleryChevronRightIcon,
    HideIcon,
    HmcIcon,
    HotIcon,
    Icon360,
    InfoIcon,
    InstagramIcon,
    KlarnaIcon,
    LeftIcon,
    LineIcon,
    LinkIcon,
    LocatorIcon,
    LogoutIcon,
    ManualsIcon,
    ManualBookIcon,
    MastercardIcon,
    MinusIcon,
    NewIcon,
    NumberAbsoluteIcon,
    NumberIcon,
    NutribulletBlueIcon,
    OvalIcon,
    PasswordFingerIcon,
    PaymentIcon,
    PaypalIcon,
    PlayIcon,
    PlusIcon,
    PodsIcon,
    ProductCountIcon,
    ProductRegistrationIcon,
    PromoCopyIcon,
    PromoIcon,
    RadioIcon,
    RefreshIcon,
    ReturnIcon,
    RightIcon,
    SearchIcon,
    SeeIcon,
    SendIcon,
    SettingsIcon,
    ShareIcon,
    TouchIcon,
    ShippingIcon,
    ShippingBoxIcon,
    FreeDeliveryIcon,
    StarIcon,
    SuccessIcon,
    FeedbackSuccessIcon,
    FeedbackWarningIcon,
    GalleryBucketFeaturesIcon,
    GalleryBucketInTheBoxIcon,
    GalleryBucketIntroIcon,
    GalleryBucketProductIcon,
    SupportIcon,
    TelephoneIcon,
    TimeIcon,
    TruckIcon,
    TickCircleFilledIcon,
    TickCircleEmptyIcon,
    TwintIcon,
    UpDownIcon,
    UpIcon,
    VerticalLineIcon,
    HorizontalLineIcon,
    VisaIcon,
    WarningIcon,
    WebsiteIcon,
    WarrantyReturnIcon,
    YoutubeIcon,
    ChronoIcon,
    ProgressBarIcon,
    CloseLargerIcon, // only in kenwood
    PlayBiggerIcon, // only in kenwood
    ChefBigIcon, // kenwood, braun, nutribullet
    SliderChervonLeft, // only in kenwood
    SliderChevronRight, // only in kenwood
    AttachmentsIcon, // only in kenwood
    DessertIcon, // only in kenwood
    DoughIcon, // only in kenwood
    DrinkIcon, // only in kenwood
    NumberConfiguratorIcon, // only in kenwood
    OthersIcon, // only in kenwood
    SecondCourseIcon, // only in kenwood
    HamburgerGlassIcon, // only in kenwood
    ReturnsIcon, // only in kenwood
    SpecialOfferIcon, // only in kenwood
    ZoomInIcon, // only in kenwood
    ZoomOutIcon, // only in kenwood
    ScrollDownIcon, // only in kenwood
    BoxIcon, // only in kenwood
    ContactIcon, // only in kenwood
    CreditCardIcon, // only in kenwood
    DeliveryIcon, // only in kenwood
    FastCartIcon, // only in kenwood
    LockIcon, // only in kenwood
    MailIcon, // only in kenwood
    PhoneIcon, // only in kenwood
    ProductTipsIcon, // only in kenwood
    PromotionsIcon, // only in kenwood
    RegistrationHistoryIcon, // only in kenwood
    StoresIcon, // only in kenwood
    SupportCartIcon, // only in kenwood
    TrackOrderIcon, // only in kenwood
    WorldIcon, // only in kenwood
    FavouriteHoverIcon, // only in kenwood
    PrintIcon, // only in kenwood
    StarFilledIcon, // only in kenwood
    StarHoverIcon, // only in kenwood
    CardIcon, // only in kenwood
    CardPreviewIcon, // only in kenwood
    ClockIcon, // only in kenwood
    CompletedIcon, // only in kenwood
    GiftIcon, // only in kenwood
    GlobeIcon, // only in kenwood
    HistoryIcon, // only in kenwood
    ImageIcon, // only in kenwood
    ListIcon, // only in kenwood
    LiveChatIcon, // only in kenwood
    ManualIcon, // only in kenwood
    MapIcon, // only in kenwood
    MarkerIcon, // only in kenwood
    PinIcon, // only in kenwood
    ViewFinderIcon, // only in kenwood
    WishlistIcon, // only in kenwood
    CaloriesIcon, // only in kenwood
    CutleryIcon, // kenwood, braun, nutribullet
    DosesIcon, // only in kenwood
    PlateIcon, // only in kenwood
    CardPaymentIcon, // only in kenwood
    SaveIcon, // only in kenwood
    SaveIconAlternative, // only in kenwood
    SavedIcon, // only in kenwood
    //ONLY IN BRAUN
    RightLightBackgroundIcon,
    RightDarkBackgroundIcon,
    LeftDarkBackgroundIcon,
    LeftLightBackgroundIcon,
    CloseLabelIcon,
    InfoLabelIcon,
    RenovaLogoIcon,
    AddToCartIcon,
    AddToCartModalCheckIcon,
    CopyLinkIcon,
    CloudIcon,
    BeanIcon,
    SizeIcon,
} = {...sharedIcons, ...overridesIcons}
